<template>
  <transition name="ll-drawer">
    <aside v-if="open" class="ll-drawer" aria-label="Loan chat">
      <div class="ll-drawer__header">
        <h2 class="ll-drawer__title">
          <span class="gs-badge-icon"><i class="fa-duotone fa-messages"></i></span>
          LoanLaunch Chat
        </h2>
        <div class="ll-drawer__head-actions">
          <button type="button" class="ll-drawer__icon-btn" title="Refresh" @click="loadMessages(true)">
            <i :class="['fa-duotone fa-arrows-rotate', { 'fa-spin': isFetching }]"></i>
          </button>
          <button type="button" class="ll-drawer__icon-btn" title="Close" @click="close">
            <i class="fa-solid fa-xmark"></i>
          </button>
        </div>
      </div>

      <p class="ll-drawer__sub">
        <template v-if="loanNumber">Loan # {{ loanNumber }}</template>
        <template v-else>This loan</template>
      </p>

      <div ref="listShell" class="ll-drawer__body">
        <p v-if="!loanId" class="ll-drawer__empty">Open a loan to see its chat.</p>
        <p v-else-if="!messages.length && isFetching" class="ll-drawer__empty">Loading messages…</p>
        <p v-else-if="!messages.length" class="ll-drawer__empty">No messages yet.</p>

        <div v-for="m in messages"
             :key="m.id"
             :class="['ll-msg', { 'll-msg--mine': m.userName === currentUser }]">
          <div class="ll-msg__top">
            <span class="ll-msg__name">{{ m.userName }}</span>
            <span class="ll-msg__date">{{ formatSent(m.sent) }}</span>
          </div>
          <p v-if="msgText(m)" class="ll-msg__text">{{ msgText(m) }}</p>
          <chat-image-gallery v-if="imagesEnabled"
                              :attachments="attachmentsFor(m)"
                              @loaded="onImageLoaded"></chat-image-gallery>
        </div>
      </div>

      <div class="ll-drawer__foot"
           @paste="onComposePaste"
           @dragover="onComposeDragOver"
           @dragleave="onComposeDragLeave"
           @drop="onComposeDrop">
        <chat-image-composer ref="imgComposer"
                             v-model="stagedImages"
                             :enabled="imagesEnabled"
                             :disabled="!loanId || isSending"></chat-image-composer>
        <p v-if="sendError" class="ll-drawer__error" role="alert">{{ sendError }}</p>
        <div class="ll-drawer__foot-row">
          <textarea v-model="newMessageText"
                    class="ll-drawer__input"
                    rows="2"
                    placeholder="Enter your message here"
                    :disabled="!loanId || isSending"
                    @keydown.enter.exact.prevent="send"></textarea>
          <button v-if="imagesEnabled"
                  type="button"
                  class="ll-drawer__icon-btn ll-drawer__attach"
                  title="Attach images"
                  :disabled="!loanId || isSending || stagedImages.length >= maxImages"
                  @click="$refs.imgComposer.openPicker()">
            <i class="fa-duotone fa-image"></i>
          </button>
          <button type="button"
                  class="ll-drawer__send"
                  title="Send message"
                  :disabled="(!newMessageText && !stagedImages.length) || !loanId || isSending"
                  @click="send">
            <i :class="isSending ? 'fa-duotone fa-spinner-third fa-spin' : 'fa-duotone fa-paper-plane-top'"></i>
          </button>
        </div>
      </div>
    </aside>
  </transition>
</template>

<script>
  import apiService from '../api/api.service.js';
  import auth from '../authConfig.js';
  import loanHelpers from '../helpers/loanHelpers.js';
  import {
    MAX_IMAGES,
    isChatImagesEnabled,
    fetchAttachmentsByMessage,
    displayMessageText,
    idKey,
    sendChatMessage
  } from '../helpers/chatImages.js';
  import ChatImageComposer from './chat/ChatImageComposer.vue';
  import ChatImageGallery from './chat/ChatImageGallery.vue';
  import {
    CHAT_KIND_LOANLAUNCH,
    onChatOpen,
    offChatOpen,
    publishChatUnread
  } from '@/helpers/chatLauncher.js';

  // How often the drawer re-pulls while it is open. ConsumerPortalView polls its own copy
  // every 3 minutes; this matches so the two never disagree for long.
  const POLL_MS = 180000;

  export default {
    name: 'LoanChatDrawer',
    components: {
      'chat-image-composer': ChatImageComposer,
      'chat-image-gallery': ChatImageGallery
    },
    data() {
      return {
        open: false,
        messages: [],
        newMessageText: '',
        isFetching: false,
        isSending: false,
        currentUser: '',
        loanData: null,
        pollId: null,
        // Chat images (ConsumerAPI MessageAttachments); all inert while imagesEnabled is false.
        imagesEnabled: false,
        attachmentsByMessage: {},
        stagedImages: [],
        sendError: '',
        maxImages: MAX_IMAGES,
        pinScrollUntil: 0
      };
    },
    computed: {
      // The drawer is always scoped to the loan in the route — this is the whole difference
      // from SwiftProcessingSPA's pipeline-wide chat list.
      loanId() {
        return this.$route.query.id;
      },
      loanNumber() {
        return this.loanData?.loanNumber || sessionStorage.getItem('loannumber') || '';
      },
      loanBorrowers() {
        return this.loanData ? loanHelpers.getLoanBorrowers(this.loanData) : [];
      },
      // header.vue already loads the file's contacts into the store, so the loan officer's
      // address for the notification comes from there rather than a second fetch.
      loanOfficerEmail() {
        const contacts = this.$store.state.loanContacts || [];
        return contacts.find(c => c.role === 'Loan Officer')?.email || '';
      }
    },
    mounted() {
      onChatOpen(this.onChatOpen);
    },
    beforeUnmount() {
      offChatOpen(this.onChatOpen);
      this.stopPolling();
    },
    watch: {
      '$route.query.id'() {
        this.messages = [];
        this.loanData = null;
        this.attachmentsByMessage = {};
        this.stagedImages = [];
        this.sendError = '';
        if (this.open) this.boot();
      }
    },
    methods: {
      onChatOpen(kind) {
        if (kind !== CHAT_KIND_LOANLAUNCH) return;
        this.open = true;
        this.boot();
      },

      close() {
        this.open = false;
        // The composer unmounts with the drawer and revokes its preview URLs.
        this.stagedImages = [];
        this.stopPolling();
      },

      async boot() {
        if (!this.loanId) return;
        const enabledCheck = this.checkImagesEnabled();
        await this.loadLoan();
        await enabledCheck;
        await this.loadMessages(false);
        this.markRead();
        this.startPolling();
      },

      startPolling() {
        this.stopPolling();
        this.pollId = setInterval(() => this.loadMessages(false), POLL_MS);
      },

      stopPolling() {
        if (this.pollId) {
          clearInterval(this.pollId);
          this.pollId = null;
        }
      },

      async loadLoan() {
        if (this.loanData || !this.loanId) return;
        try {
          const tokenResponse = await auth.acquireTokenResponse();
          const res = await apiService.GetLoan(this.loanId, tokenResponse.accessToken);
          this.loanData = res.data.data;
        } catch (error) {
          // Non-fatal: without it the thread still reads, only the send notification
          // loses its borrower recipients.
          console.error('[LoanChatDrawer] Failed to load loan:', error);
        }
      },

      async loadMessages(scrollAfter) {
        if (!this.loanId) return;
        this.isFetching = true;
        try {
          const tokenResponse = await auth.acquireTokenResponse();
          this.currentUser = tokenResponse.account?.name || '';
          const res = await apiService.GetMessagesByLoanId(this.loanId, tokenResponse.accessToken);
          this.applyMessages(res.data.data, scrollAfter);
        } catch (error) {
          console.error('[LoanChatDrawer] Failed to fetch messages:', error);
        } finally {
          this.isFetching = false;
        }
      },

      // Same shape ConsumerPortalView's processMessages produces, so both views agree.
      applyMessages(data, scrollAfter) {
        const rows = (data || []).map(m => ({ ...m, sent: new Date(m.sent) }));
        rows.sort((a, b) => a.sent - b.sent);
        this.messages = rows;

        publishChatUnread(rows.filter(m => m.isread === false).length);

        if (scrollAfter !== false) this.scrollToLatest();

        // Presigned URLs expire (~15 min), so attachments are re-pulled with every message load.
        this.loadAttachments();
      },

      async checkImagesEnabled() {
        try {
          const tokenResponse = await auth.acquireTokenResponse();
          this.imagesEnabled = await isChatImagesEnabled(tokenResponse.accessToken);
        } catch (error) {
          this.imagesEnabled = false;
        }
      },

      async loadAttachments() {
        if (!this.imagesEnabled || !this.loanId) return;
        const loanId = this.loanId;
        try {
          const tokenResponse = await auth.acquireTokenResponse();
          const map = await fetchAttachmentsByMessage(loanId, tokenResponse.accessToken, this.attachmentsByMessage);
          if (loanId === this.loanId) this.attachmentsByMessage = map;
        } catch (error) {
          console.error('[LoanChatDrawer] Failed to fetch chat images:', error);
        }
      },

      msgText(m) {
        return displayMessageText(m, this.attachmentsByMessage);
      },

      attachmentsFor(m) {
        return this.attachmentsByMessage[idKey(m.id)] || [];
      },

      // Images load after the list is laid out, so re-pin to the bottom as they arrive —
      // right after a scrollToLatest, or when the reader was already at the bottom.
      onImageLoaded() {
        const shell = this.$refs.listShell;
        if (!shell) return;
        const nearBottom = shell.scrollHeight - shell.scrollTop - shell.clientHeight < 240;
        if (Date.now() < this.pinScrollUntil || nearBottom) shell.scrollTop = shell.scrollHeight;
      },

      onComposePaste(evt) {
        this.$refs.imgComposer?.onPaste(evt);
      },

      onComposeDragOver(evt) {
        this.$refs.imgComposer?.onDragOver(evt);
      },

      onComposeDragLeave(evt) {
        this.$refs.imgComposer?.onDragLeave(evt);
      },

      onComposeDrop(evt) {
        this.$refs.imgComposer?.onDrop(evt);
      },

      scrollToLatest() {
        this.pinScrollUntil = Date.now() + 5000;
        this.$nextTick(() => {
          const shell = this.$refs.listShell;
          if (shell) shell.scrollTop = shell.scrollHeight;
        });
      },

      async markRead() {
        try {
          const tokenResponse = await auth.acquireTokenResponse();
          const azureId = tokenResponse.uniqueId || localStorage.getItem('azure_id');
          if (!azureId || !this.loanId) return;
          await apiService.MarkMessagesRead(azureId, this.loanId, tokenResponse.accessToken);
          publishChatUnread(0);
        } catch (error) {
          console.error('[LoanChatDrawer] Failed to mark messages read:', error);
        }
      },

      // Mirrors ConsumerPortalView.onCreateMessage/createMessage exactly, so a message sent
      // from the rail is indistinguishable from one sent on the LoanLaunch page.
      async send() {
        const text = (this.newMessageText || '').trim();
        const files = this.imagesEnabled ? this.stagedImages.map(i => i.file) : [];
        if ((!text && !files.length) || !this.loanId || this.isSending) return;

        this.isSending = true;
        this.sendError = '';
        try {
          const tokenResponse = await auth.acquireTokenResponse();
          const request = {
            message: {
              id: null,
              loanId: this.loanId,
              messageText: text,
              userName: tokenResponse.account.name
            },
            notiDto: {
              message: text,
              loanNumber: this.loanData?.loanNumber,
              userEmail: tokenResponse.account.username,
              loanId: this.loanId,
              senderName: tokenResponse.account.name,
              senderEmail: this.loanOfficerEmail,
              recipients: this.loanBorrowers.map(b => ({
                borrowerName: b.name,
                borrowerEmail: b.email
              }))
            }
          };

          // With no images this is the same UpsertMessage call as before.
          const res = await sendChatMessage({ request, files, token: tokenResponse.accessToken });
          this.newMessageText = '';
          this.stagedImages = [];
          this.applyMessages(res.data.data, true);
        } catch (error) {
          console.error('[LoanChatDrawer] Failed to post message:', error);
          // Text and staged images are kept so the user can retry.
          this.sendError = error?.message || 'Failed to send message.';
        } finally {
          this.isSending = false;
        }
      },

      formatSent(d) {
        if (!d) return '';
        return d.toLocaleString('en-US', {
          month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit'
        });
      }
    }
  };
</script>

<style scoped>
  .ll-drawer {
    position: fixed;
    top: 74px;
    right: 18px;
    bottom: 18px;
    width: 400px;
    max-width: calc(100vw - 36px);
    z-index: 1000;
    display: flex;
    flex-direction: column;
    background: var(--gs-card, #fff);
    border: 1px solid var(--gs-border, #e7ebf3);
    border-radius: var(--gs-radius, 12px);
    box-shadow: 0 16px 40px rgba(17, 40, 80, .16);
    overflow: hidden;
  }

  .ll-drawer__header {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px 14px 4px;
  }

  .ll-drawer__title {
    display: flex;
    align-items: center;
    gap: 10px;
    margin: 0;
    font-size: 1.5rem;
    font-weight: 700;
    color: var(--gs-ink, #16305a);
  }

  .ll-drawer__head-actions {
    margin-left: auto;
    display: flex;
    align-items: center;
    gap: 4px;
  }

  .ll-drawer__icon-btn {
    border: none;
    background: transparent;
    color: var(--gs-muted, #8a95a8);
    cursor: pointer;
    padding: 6px 8px;
    border-radius: 8px;
    font-size: 1.4rem;
  }

  .ll-drawer__icon-btn:hover {
    background: #f2f5fa;
    color: var(--gs-ink, #16305a);
  }

  .ll-drawer__sub {
    margin: 0;
    padding: 0 14px 10px;
    font-size: 1.15rem;
    color: var(--gs-muted, #8a95a8);
    border-bottom: 1px solid var(--gs-border-soft, #eef1f7);
  }

  .ll-drawer__body {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    padding: 12px 14px;
    display: flex;
    flex-direction: column;
    gap: 9px;
  }

  .ll-drawer__empty {
    margin: 18px 0;
    text-align: center;
    font-size: 1.25rem;
    color: var(--gs-muted, #8a95a8);
  }

  /* Messages hug the bottom of the pane like a chat log. margin-top on the first child
     (rather than justify-content) keeps the scroll intact once they overflow. */
  .ll-msg:first-of-type {
    margin-top: auto;
  }

  .ll-msg {
    background: #f2f5fa;
    border-radius: var(--gs-radius-sm, 8px);
    padding: 9px 11px;
  }

  .ll-msg--mine {
    background: var(--gs-blue-soft, #e9f0ff);
  }

  .ll-msg__top {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 10px;
  }

  .ll-msg__name {
    font-size: 1.2rem;
    font-weight: 700;
    color: var(--gs-ink, #16305a);
  }

  .ll-msg__date {
    font-size: 1.1rem;
    color: var(--gs-blue, #2f6fed);
    white-space: nowrap;
  }

  .ll-msg__text {
    margin: 5px 0 0;
    font-size: 1.25rem;
    line-height: 1.4;
    color: var(--gs-ink-soft, #46566f);
    white-space: pre-wrap;
    overflow-wrap: anywhere;
  }

  .ll-drawer__foot {
    flex: 0 0 auto;
    padding: 11px 14px;
    border-top: 1px solid var(--gs-border-soft, #eef1f7);
  }

  .ll-drawer__foot-row {
    display: flex;
    align-items: flex-end;
    gap: 9px;
  }

  .ll-drawer__attach {
    flex: 0 0 auto;
    height: 36px;
  }

  .ll-drawer__icon-btn:disabled {
    opacity: .45;
    cursor: not-allowed;
  }

  .ll-drawer__error {
    margin: 0 0 6px;
    font-size: 1.15rem;
    color: #c62828;
  }

  .ll-drawer__input {
    flex: 1 1 auto;
    min-width: 0;
    resize: none;
    border: 1px solid var(--gs-border, #e7ebf3);
    border-radius: 9px;
    padding: 9px 11px;
    font-family: inherit;
    font-size: 1.25rem;
    color: var(--gs-ink, #16305a);
  }

  .ll-drawer__input:focus {
    outline: none;
    border-color: var(--gs-blue, #2f6fed);
  }

  .ll-drawer__send {
    flex: 0 0 auto;
    border: none;
    background: var(--gs-navy, #15305a);
    color: #fff;
    width: 36px;
    height: 36px;
    border-radius: 9px;
    cursor: pointer;
    font-size: 1.4rem;
  }

  .ll-drawer__send:disabled {
    opacity: .45;
    cursor: not-allowed;
  }

  .ll-drawer-enter-active,
  .ll-drawer-leave-active {
    transition: opacity .18s ease, transform .18s ease;
  }

  .ll-drawer-enter-from,
  .ll-drawer-leave-to {
    opacity: 0;
    transform: translateX(12px);
  }

  @media (prefers-reduced-motion: reduce) {
    .ll-drawer-enter-active,
    .ll-drawer-leave-active {
      transition: none;
    }
  }

  @media (max-width: 630px) {
    .ll-drawer {
      top: 12px;
      right: 8px;
      left: 8px;
      bottom: 8px;
      width: auto;
    }
  }
</style>
